import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { mappingAPI, offersAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ModelAliasReview from '../components/ModelAliasReview';
import { PageHeader, SearchField, Alert, FileButton, Icon, SourceDot, fmtNum, fmtMoney } from '../ui';
import './MappingPage.css';

const MappingPage = () => {
    const [mappings, setMappings] = useState([]);
    const [sources, setSources] = useState([]);   // [{source_key, title}]
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [search, setSearch] = useState('');

    // Manual add
    const [newMyId, setNewMyId] = useState('');
    const [newSource, setNewSource] = useState('');
    const [newCompId, setNewCompId] = useState('');

    // Bulk source (for 2-column files)
    const [bulkSource, setBulkSource] = useState('');

    const loadMappings = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await mappingAPI.listAll();
            setMappings(data.mappings || []);
        } catch (err) {
            console.error(err);
            setError('Не вдалося завантажити зіставлення.');
        } finally {
            setLoading(false);
        }
    };

    // Джерела беруться з реєстру Source — раніше тут був хардкод
    // ['infoshina','ukrshina'], через який tshina/rezina_ua неможливо було
    // зіставити, а отже вони не потрапляли в порівняння цін.
    useEffect(() => {
        (async () => {
            try {
                const data = await offersAPI.sources();
                const active = (data.sources || []).filter(s => s.is_active);
                setSources(active);
                if (active.length) {
                    setNewSource(prev => prev || active[0].source_key);
                    setBulkSource(prev => prev || active[0].source_key);
                }
            } catch (err) {
                console.error('Failed to load sources:', err);
            }
        })();
    }, []);

    useEffect(() => { loadMappings(); }, []);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setMessage(null);
        setError(null);
        setLoading(true);
        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            if (rows.length < 2) throw new Error('У файлі немає рядків з даними.');

            // Detect: 3-column (my_id, source, competitor_id) or 2-column (my_id, competitor_id) + bulkSource
            const header = rows[0].map(h => String(h).toLowerCase().trim());
            const sourceIdx = header.findIndex(h => h.includes('source') || h.includes('источник') || h.includes('джерело'));

            const triples = [];
            for (let i = 1; i < rows.length; i++) {
                const row = rows[i];
                if (!row || row[0] === undefined) continue;
                if (sourceIdx !== -1) {
                    // 3-column: my_id, source, competitor_id (source in its column)
                    const cols = [0, 1, 2].filter(c => c !== sourceIdx);
                    const myId = parseInt(row[cols[0]], 10);
                    const compId = parseInt(row[cols[1]], 10);
                    const src = String(row[sourceIdx]).trim().toLowerCase();
                    if (!isNaN(myId) && !isNaN(compId) && src) triples.push([myId, src, compId]);
                } else {
                    // 2-column: my_id, competitor_id + selected bulkSource
                    const myId = parseInt(row[0], 10);
                    const compId = parseInt(row[1], 10);
                    if (!isNaN(myId) && !isNaN(compId)) triples.push([myId, bulkSource, compId]);
                }
            }

            if (triples.length === 0) throw new Error('Не знайдено жодного коректного рядка.');

            const res = await mappingAPI.bulk(triples);
            setMessage(`Збережено зіставлень: ${res.count}.`);
            await loadMappings();
        } catch (err) {
            console.error(err);
            setError(err.message || 'Не вдалося завантажити файл.');
        } finally {
            setLoading(false);
            e.target.value = '';
        }
    };

    const handleAdd = async () => {
        if (!newMyId || !newCompId) return;
        setError(null); setMessage(null);
        try {
            await mappingAPI.add(parseInt(newMyId, 10), newSource, parseInt(newCompId, 10));
            setNewMyId(''); setNewCompId('');
            setMessage('Зіставлення додано.');
            await loadMappings();
        } catch (err) {
            console.error(err);
            setError('Не вдалося додати зіставлення.');
        }
    };

    const handleDelete = async (myId, source) => {
        try {
            await mappingAPI.remove(myId, source);
            await loadMappings();
        } catch (err) {
            console.error(err);
            setError('Не вдалося видалити.');
        }
    };

    const filtered = mappings.filter(m => {
        if (!search) return true;
        const s = search.toLowerCase();
        if (String(m.my_id).includes(s)) return true;
        return Object.values(m.sources).some(v =>
            String(v.competitor_id).includes(s) || (v.name || '').toLowerCase().includes(s)
        );
    });

    return (
        <div className="ui-page">
            <PageHeader
                title="Зіставлення"
                description={<>
                    Зіставлення твоїх ID товарів з ID у конкурентів
                    {sources.length > 0 && ` (${sources.map(s => s.title).join(', ')})`}.
                    По одному зіставленню на кожного конкурента для кожного товару.
                </>}
            />

            {message && <Alert kind="ok" onClose={() => setMessage(null)}>{message}</Alert>}
            {error && <Alert kind="error" onClose={() => setError(null)}>{error}</Alert>}

            <ModelAliasReview />

            <div className="map-forms">
                {/* Bulk upload */}
                <section className="ui-card">
                    <h3 style={{ margin: 0 }}>Масове завантаження</h3>
                    <p className="muted" style={{ fontSize: 13, margin: '8px 0 16px' }}>
                        Excel з двома колонками (твій ID, ID конкурента) — джерело обери нижче.
                        Або три колонки із заголовком «source».
                    </p>
                    <div className="ui-toolbar">
                        <select value={bulkSource} onChange={e => setBulkSource(e.target.value)} aria-label="Джерело">
                            {sources.map(s => <option key={s.source_key} value={s.source_key}>{s.title}</option>)}
                        </select>
                        <FileButton primary accept=".xlsx,.xls,.csv" onChange={handleUpload}>
                            Завантажити Excel
                        </FileButton>
                    </div>
                </section>

                {/* Manual add */}
                <section className="ui-card">
                    <h3 style={{ margin: 0 }}>Додати вручну</h3>
                    <p className="muted" style={{ fontSize: 13, margin: '8px 0 16px' }}>
                        ID конкурента видно в Excel-вивантаженні на сторінці джерела.
                    </p>
                    <div className="ui-toolbar">
                        <input type="number" placeholder="Твій ID" aria-label="Твій ID" value={newMyId}
                               onChange={e => setNewMyId(e.target.value)} style={{ width: 120 }} />
                        <select value={newSource} onChange={e => setNewSource(e.target.value)} aria-label="Джерело">
                            {sources.map(s => <option key={s.source_key} value={s.source_key}>{s.title}</option>)}
                        </select>
                        <input type="number" placeholder="ID конкурента" aria-label="ID конкурента" value={newCompId}
                               onChange={e => setNewCompId(e.target.value)} style={{ width: 150 }} />
                        <button onClick={handleAdd} className="ui-btn ui-btn--primary" disabled={!newMyId || !newCompId}>
                            <Icon name="plus" size={18} /> Додати
                        </button>
                    </div>
                </section>
            </div>

            <section className="ui-card ui-card--flush">
                <div className="ui-card-head">
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                        <h2 className="ui-section-title" style={{ fontSize: 20 }}>Ручні зіставлення</h2>
                        <span className="muted num" style={{ fontSize: 14 }}>{fmtNum(mappings.length)} товарів</span>
                    </div>
                    <SearchField value={search} onChange={setSearch} placeholder="Пошук за ID або назвою" style={{ minWidth: 260 }} />
                </div>

                {loading ? <LoadingSpinner /> : (
                    <div className="ui-table-wrap" style={{ marginTop: 8 }}>
                        <table className="ui-table">
                            <thead>
                                <tr>
                                    <th>Мій ID</th>
                                    {sources.map((s, i) => (
                                        <th key={s.source_key}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                <SourceDot sourceKey={s.source_key} index={i} />{s.title}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr><td colSpan={sources.length + 1} className="muted">Зіставлень поки немає.</td></tr>
                                ) : filtered.map((m, i) => (
                                    <tr key={i} style={{ verticalAlign: 'top' }}>
                                        <td style={{ fontWeight: 700 }}>{m.my_id}</td>
                                        {sources.map(s => {
                                            const src = s.source_key;
                                            const v = m.sources[src];
                                            return (
                                                <td key={src} style={{ minWidth: 200 }}>
                                                    {v ? (
                                                        <div className="map-cell">
                                                            <div className="muted" style={{ fontSize: 12 }}>ID {v.competitor_id}</div>
                                                            <div style={{ fontSize: 13 }}>
                                                                {v.name ? (v.brand && !v.name.startsWith(v.brand) ? `${v.brand} ${v.name}` : v.name) : <span style={{ color: 'var(--bad)' }}>немає в базі</span>}
                                                            </div>
                                                            <div style={{ fontSize: 13, fontWeight: 600 }}>
                                                                {v.price != null
                                                                    ? fmtMoney(v.price)
                                                                    : v.in_stock === 0
                                                                        ? <span className="muted" style={{ fontWeight: 400 }}>немає в наявності</span>
                                                                        : ''}
                                                            </div>
                                                            <button onClick={() => handleDelete(m.my_id, src)} className="ui-btn ui-btn--danger ui-btn--sm"
                                                                    style={{ alignSelf: 'flex-start', marginTop: 4 }}>
                                                                Видалити
                                                            </button>
                                                        </div>
                                                    ) : <span className="muted">—</span>}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
};

export default MappingPage;
